import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, CircleCheck, FileSpreadsheet, History, ShoppingCart } from 'lucide-react';
import { ETAPAS, oQueFalta, quandoFoi } from '@/features/cotacoes/situacao';
import { Cartao, ErroDoBloco, Esqueleto } from './componentes';
import type { ResumoCompras, ResumoFolha, Tom } from './consultas';
import { cn } from '@/lib/utils';

type Pendencia = {
  chave: string;
  modulo: 'Cotação' | 'Folha';
  titulo: string;
  detalhe: string;
  tom: Tom;
  destino: string;
  em: string | null;
};

type Estado<T> = { alcanca: boolean; dados?: T; carregando: boolean; erro: boolean };

const ICONE = { Cotação: ShoppingCart, Folha: FileSpreadsheet } as const;

/** Acima disso a lista esconde o resto atrás de um botão: a página inicial não pode virar a listagem inteira. */
const VISIVEIS = 5;

/** Cotação aberta vira pendência com o mesmo rótulo, frase e destino da listagem de cotações. */
function daCotacao(cotacao: ResumoCompras['abertas'][number]): Pendencia {
  const etapa = ETAPAS[cotacao.situacao.etapa];

  return {
    chave: `cotacao-${cotacao.id}`,
    modulo: 'Cotação',
    titulo: `Cotação nº ${cotacao.numero} · ${etapa.rotulo}`,
    detalhe: oQueFalta(cotacao.situacao),
    tom: etapa.tom === 'espera' ? 'espera' : 'acao',
    destino: `/cotacoes/${cotacao.id}/${etapa.destino}`,
    em: cotacao.situacao.ultima_acao.em,
  };
}

/** Mais recente primeiro; sem data (cadastro a acertar) vai para o fim do grupo. */
function porData(a: Pendencia, b: Pendencia): number {
  return (b.em ?? '').localeCompare(a.em ?? '');
}

/**
 * O que está em cima da mesa, de todos os módulos: primeiro o que depende de
 * quem está usando o sistema, depois o que espera fornecedor ou escritório.
 * Cada linha leva direto para a tela onde a coisa se resolve.
 */
export default function ContinuarDeOndeParou({
  compras,
  folha,
  className,
}: {
  compras: Estado<ResumoCompras>;
  folha: Estado<ResumoFolha>;
  className?: string;
}) {
  const carregando = (compras.alcanca && compras.carregando) || (folha.alcanca && folha.carregando);

  const pendencias: Pendencia[] = [
    ...(compras.dados?.abertas ?? []).map(daCotacao),
    ...(folha.dados?.pendencias ?? []).map((p, i) => ({ ...p, chave: `folha-${i}`, modulo: 'Folha' as const })),
  ];
  const suas = pendencias.filter((p) => p.tom === 'acao').sort(porData);
  const aguardando = pendencias.filter((p) => p.tom === 'espera').sort(porData);

  const ultima = ultimaMovimentacao(compras.dados, folha.dados);

  return (
    <Cartao
      titulo="Continuar de onde parou"
      subtitulo={
        ultima ? (
          <span className="inline-flex items-center gap-xs">
            <History size={13} className="shrink-0" />
            Última movimentação: {ultima.texto} · {ultima.soData ? `em ${diaMes(ultima.em)}` : quandoFoi(ultima.em)}
          </span>
        ) : (
          'O que está em andamento nos módulos, do que depende de você ao que depende dos outros.'
        )
      }
      className={className}
    >
      {carregando && pendencias.length === 0 ? (
        <div className="space-y-sm">
          <Esqueleto className="h-[52px] w-full" />
          <Esqueleto className="h-[52px] w-full" />
          <Esqueleto className="h-[52px] w-full" />
        </div>
      ) : (
        <>
          {compras.erro && <ErroDoBloco oQue="as cotações" />}
          {folha.erro && <ErroDoBloco oQue="a folha" />}

          {pendencias.length === 0 && !compras.erro && !folha.erro ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-md border border-dashed border-border px-md py-xl text-center">
              <CircleCheck size={22} className="text-success" />
              <p className="mt-sm text-[14px] font-bold text-ink">Nada pendente</p>
              <p className="text-[13px] text-muted">Quando uma cotação ou uma folha estiver em andamento, ela aparece aqui.</p>
            </div>
          ) : (
            <div className="space-y-md">
              {suas.length > 0 && <Grupo titulo="Depende de você" pendencias={suas} />}
              {aguardando.length > 0 && <Grupo titulo="Aguardando outros" pendencias={aguardando} />}
            </div>
          )}
        </>
      )}
    </Cartao>
  );
}

function Grupo({ titulo, pendencias }: { titulo: string; pendencias: Pendencia[] }) {
  const [todas, setTodas] = useState(false);
  const mostradas = todas ? pendencias : pendencias.slice(0, VISIVEIS);
  const escondidas = pendencias.length - mostradas.length;

  return (
    <div>
      <h3 className="mb-xs text-[11px] font-bold tracking-[0.08em] text-muted uppercase">
        {titulo} · {pendencias.length}
      </h3>
      <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
        {mostradas.map((pendencia) => {
          const Icone = ICONE[pendencia.modulo];

          return (
            <li key={pendencia.chave}>
              <Link
                to={pendencia.destino}
                className="group flex items-center gap-sm px-sm py-sm transition-colors hover:bg-surface-subtle"
              >
                <span
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-md',
                    pendencia.tom === 'acao' ? 'bg-accent/10 text-accent' : 'bg-warning/10 text-warning',
                  )}
                >
                  <Icone size={16} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-bold text-ink">{pendencia.titulo}</span>
                  <span className="block truncate text-[12px] text-muted">
                    {pendencia.detalhe}
                    {pendencia.em ? ` · ${quandoFoi(pendencia.em)}` : ''}
                  </span>
                </span>
                <ChevronRight size={16} className="shrink-0 text-muted group-hover:text-accent" />
              </Link>
            </li>
          );
        })}
      </ul>
      {escondidas > 0 && (
        <button type="button" onClick={() => setTodas(true)} className="mt-xs text-[13px] font-bold text-accent hover:underline">
          Mostrar mais {escondidas}
        </button>
      )}
    </div>
  );
}

/** O último passo dado no sistema: a ação mais recente de uma cotação aberta, um fechamento ou a folha importada. */
function ultimaMovimentacao(compras?: ResumoCompras, folha?: ResumoFolha): Movimentacao | null {
  const eventos: Movimentacao[] = [];

  for (const cotacao of compras?.abertas ?? []) {
    const { texto, em } = cotacao.situacao.ultima_acao;
    if (em) eventos.push({ texto: `${texto} (cotação nº ${cotacao.numero})`, em });
  }
  if (compras?.ultimo) {
    eventos.push({
      texto: `pacote da cotação nº ${compras.ultimo.numero} fechado com a ${compras.ultimo.fornecedor}`,
      // O fechamento guarda só o dia: o meio-dia ordena sem inventar horário na tela.
      em: `${compras.ultimo.data}T12:00:00`,
      soData: true,
    });
  }
  if (folha?.atual?.importado_em) {
    eventos.push({ texto: `folha de ${folha.atual.rotulo} importada`, em: folha.atual.importado_em });
  }

  const ultimo = eventos.sort((a, b) => new Date(b.em).getTime() - new Date(a.em).getTime())[0];
  if (!ultimo) return null;

  return { ...ultimo, texto: ultimo.texto.charAt(0).toUpperCase() + ultimo.texto.slice(1) };
}

type Movimentacao = { texto: string; em: string; soData?: boolean };

/** "2026-09-10T12:00:00" vira "10/09". */
function diaMes(iso: string): string {
  const [, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}`;
}
